
import React, { useMemo, useRef } from "react";
import { extractLastWord, isTypedRestaurantFilter, parseRestaurantType, restaurantTypeMatchesFilter, TypeResto } from "../../../components/types/restaurantTypes";
import {  FilterValue, formatDateToYMD, IndexablePage, isBetween, listFiltersResto, typeSchedule, typeSpecialDate } from "../../../components/types/types";
import {SearchMiniResto, SearchMiniRestoIndexed } from "../../../context/RestaurantContext";


const toMinutes = (h: unknown): number => {
  if (typeof h !== "string") {
    return NaN;
  }
  const m = h.trim().match(/^(\d{1,2})h(\d{2})$/);
  if (!m) return NaN;

  const hh = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  return hh * 60 + mm;
};
const nowMinutes = (): number => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
};
const getRangesForDate = (restaurant: FilterableRestaurant, date: Date): Array<[number, number]> => {
    const ymd = formatDateToYMD(date);
    const dayKey = baseDays[date.getDay()];
    const special = restaurant.special_date?.[ymd];
    const normalizeRange = (startRaw: unknown,endRaw: unknown): [number, number] | null => {
        const start = toMinutes(startRaw);
        let end = toMinutes(endRaw);
        if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
        if (end <= start) end += 24 * 60;
        return [start, end];
    };
    if (special) {
        const isOpen = special?.[0];
        if (isOpen !== true) return [];

        const slots = special?.[1];
        if (!Array.isArray(slots) || !Array.isArray(slots[0])) return [];

        return slots
            .map((r: any) => normalizeRange(r?.[0], r?.[1]))
            .filter((r): r is [number, number] => r !== null);
    }
    const sched = restaurant.schedule?.[dayKey];
    if (!Array.isArray(sched)) return [];
    return sched
        .map((r) => normalizeRange(r[0], r[1]))
        .filter((r): r is [number, number] => r !== null);
};
const addDays = (date: Date, days: number): Date => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};
const isStillValidForServiceFilter = (restaurant: FilterableRestaurant,service: "lunch" | "dinner" | "lunch_tomorrow" | "dinner_tomorrow" | "now"): boolean => {
    const currentDate = new Date();
    const date = service === "lunch_tomorrow" || service === "dinner_tomorrow" ? addDays(currentDate, 1) : currentDate;

    const ranges = getRangesForDate(restaurant, date);
    if (!ranges.length) return false;
    if (service === "now") {
        const current = nowMinutes();
        const tolerance = 15;

        return ranges.some(([s, e]) => {
            let adjustedStart = s - tolerance;
            if (adjustedStart < 0) adjustedStart += 24 * 60;
            if (e >= s) return adjustedStart <= current && current <= e;
            return current >= adjustedStart || current <= e;
        });
    }
    const target = (service === "lunch" || service === "lunch_tomorrow")  ? toMinutes("12h30") : toMinutes("20h00");
    const chosen = ranges.find(([s, e]) => s <= target && target <= e);
    if (!chosen) return false;
    if (service === "lunch" || service === "dinner") {
        const current = nowMinutes();
        return current <= chosen[1];
    }

    return true;
};
export const normalizeSearch = (value: string) => {
    return value
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();
};

const typeRestoNorm = TypeResto.map(value => ({
  raw: value,
  norm: normalizeSearch(value),
}));
export interface IndexablePageIndexed extends IndexablePage {
  _cityNameNorm?: string;
}
export const indexIndexablePages = (
  pages: IndexablePage[]
): IndexablePageIndexed[] => {
  return pages.map(page => ({
    ...page,
    _cityNameNorm: page.city_name ? normalizeSearch(page.city_name) : undefined,
  }));
};

export type VilleSuggestion = {
    name: string;
    latitude: string;
    longitude: string;
};
export type CurrentCity = {
    name: string;
    slug: string;

    cityNameRaw?: string;
    latitude?: string;
    longitude?: string;
} | null;

export const topCities = [
  { name: "Angers", slug: "angers", cityNameRaw: "Angers",latitude: "47.444139",longitude: "-0.563244" },
  { name: "Brissac Loire Aubance", slug: "brissac-loire-aubance",cityNameRaw: "Brissac Loire Aubance",latitude: "47.34061",longitude: "-0.43704" },
  { name: "Mûrs-Erigné", slug: "murs-erigne",cityNameRaw: "Mûrs-Erigné",latitude: "47.3992996216",longitude: "-0.537496984005" },
  { name: "Les Ponts-de-Cé", slug: "les-ponts-de-ce",cityNameRaw: "Les Ponts-de-Cé",latitude: "47.4378194",longitude: "-0.5282956" },
  { name: "Chalonnes-sur-Loire", slug: "chalonnes-sur-Loire",cityNameRaw: "Chalonnes-sur-Loire",latitude: "47.3517",longitude: "-0.7631" },
  { name: "Trélazé", slug: "trelaze",cityNameRaw: "Trélazé",latitude: "47.4463095",longitude: "-0.4964779" },
];

export const getRandomSuggestions = (miniRestos: SearchMiniRestoIndexed[],count = 7): SearchMiniRestoIndexed[] => {
    if (miniRestos.length <= count) return [...miniRestos];
    const shuffled = [...miniRestos];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j],shuffled[i]];
    }
    return shuffled.slice(0, count);
};
export const humanizeCitySlug = (slug: string): string => {
    return slug
        .split("-")
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join("-");
};

export const getInitialCurrentCity = (
    routeCity: string | undefined,
    indexablePages: IndexablePage[]
): CurrentCity | null => {
    if (!routeCity) return null;

    const match = indexablePages.find(page =>
        page.city_slug === routeCity &&
        !!page.city_name
    );

    if (match?.city_name) {
        return {
            name: match.city_name,
            slug: routeCity
        };
    }

    return {
        name: humanizeCitySlug(routeCity),
        slug: routeCity
    };
};

export const getVisibleDateHour = (date:Date|null,hour:string) => {
    if (!date && !hour) return 'Date et heure';
    let construct = '';
    const today = new Date()
    if (date) {
        const day = date.getDate();
        const month = date.toLocaleDateString('fr-FR', {
            month: 'long',
        });
        const weekDay = date.toLocaleDateString('fr-FR', {
            weekday: 'short',
        });
        const formattedWeekDay = weekDay.charAt(0).toUpperCase() + weekDay.slice(1);
        const isToday = date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
        if(isToday) construct += `Aujourd’hui`
        else construct += `${formattedWeekDay} ${day === 1 ? '1er' : day} ${month}`;

        if (hour) {
            if(hour === '12h30') construct += ` — midi`;
            else if (hour === '20h00') construct += ` — soir`;
            else construct += ` — ${hour}`;
        }
    } else if (hour) {
        if(hour === '12h30') construct += `Midi`;
        else if (hour === '20h00') construct += `Soir`;
        else construct += `${hour}`;
    }

    return construct;
}

export type MultiSearchResult =
    | {
        kind: "restaurant";
        label: string;
        restaurant: SearchMiniResto;
    };

const MAX_MULTI_SEARCH_RESULTS = 12;

type ScoredMultiSearchResult = {
    result: MultiSearchResult;
    score: number;
};

const insertTopSearchResult = (
    topResults: ScoredMultiSearchResult[],
    candidate: ScoredMultiSearchResult
): void => {
    let insertIndex = topResults.findIndex( existing => candidate.score > existing.score);
    if (insertIndex === -1) {
        if (topResults.length < MAX_MULTI_SEARCH_RESULTS) topResults.push(candidate);
        return;
    }
    topResults.splice(insertIndex, 0, candidate);
    if (topResults.length > MAX_MULTI_SEARCH_RESULTS) topResults.pop();
};

export const getMultiSearchResults = ({search,miniRestos}: {
    search: string;
    miniRestos: SearchMiniRestoIndexed[];
}): MultiSearchResult[] => {
    const cleanSearch = normalizeSearch(search);
    if (!cleanSearch) return [];
    const scoreMatch = (normalizedValue: string): number => {
        if (!normalizedValue.includes(cleanSearch)) return 0;
        if (normalizedValue.startsWith(cleanSearch))  return 3;
        const hasWordStartingWithSearch = normalizedValue.split(/\s+/).some(word => word.startsWith(cleanSearch));
        return hasWordStartingWithSearch ? 2 : 1;
    };
    const topResults: ScoredMultiSearchResult[] = [];
    const addResult = ( result: MultiSearchResult, baseScore: number) => {
        if (baseScore <= 0) return;
        insertTopSearchResult(topResults, { result, score:baseScore});
    };
    for (const resto of miniRestos) {
        const score = scoreMatch(resto._nameNorm);
        addResult({kind: "restaurant",label: resto.name,restaurant: resto},score);
    }
    return topResults.map(item => item.result);
};

export type PresentSearchFilters = {
  filters: string[];
  cuisines: string[];
  types: string[];
};
const typeRestoLastWordNorm = TypeResto.map(type => ({raw: type,normLastWord: extractLastWord(type).toLowerCase(),}));
export const getAllPresentFilterFromMiniRestos = (miniRestos: FilterableRestaurant[]): PresentSearchFilters => {
  const filters = new Set<string>();
  const cuisines = new Set<string>();
  const types = new Set<string>();

  for (const resto of miniRestos) {
    for (const rawFilter of resto.filters ?? []) {
      const filter = rawFilter?.trim();
      if (filter) filters.add(filter);
    }
    for (const rawCuisine of resto.cuisines ?? []) {
      const cuisine = rawCuisine?.trim();
      if (cuisine) cuisines.add(cuisine);
    }
    for (const rawType of resto.type ?? []) {
      if (!rawType) continue;
      const parsed = parseRestaurantType(rawType);
      if (!parsed.value) continue;
      if (parsed.kind !== "classic") {
        types.add(rawType);
        continue;
      }
      const lastWord = extractLastWord(parsed.value).toLowerCase();
      const matchingClassicType = typeRestoLastWordNorm.find(
        t => t.normLastWord.includes(lastWord)
      )?.raw;
      types.add(matchingClassicType ?? parsed.value);
    }
  }
  return {filters: [...filters],cuisines: [...cuisines],types: [...types],};
};

export const toMinutesMini = (h: string): number => {
    const m = h.trim().match(/^(\d{1,2})h(\d{2})$/);
    if (!m) return NaN;
    return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
};
const baseDays = ["Dimanche","Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", ];
const isCuisineFilter = (filter: string,presentFilters: PresentSearchFilters) => {
    return presentFilters.cuisines.includes(filter);
};
export const normalizeCitySearch = (value: string) => {
    return normalizeSearch(value)
        .replace(/[-']/g, " ")
        .replace(/\s+/g, " ")
        .trim();
};
export type FilterableRestaurant = {
    filters?: string[];
    cuisines?: string[];
    type?: string[];
    note: number;
    price_range?:[number,number],
    schedule?: typeSchedule;
    special_date?: typeSpecialDate;
};
export const getContextualMiniRestos = (miniRestos: SearchMiniRestoIndexed[],currentCity: CurrentCity | null,currentDate: Date | null,currentHour: string,coordTemp?: {latitude: string;longitude: string;},proximityDistance: number | null = null,  currentNeighborhood: string[] = []
): SearchMiniRestoIndexed[] => {
    const isProximitySearch = currentCity?.slug === "restaurants-a-proximite";

    const normalizedCity =
        currentCity && !isProximitySearch
            ? normalizeCitySearch(currentCity.name)
            : null;

    const userLat = coordTemp ? Number(coordTemp.latitude) : NaN;
    const userLon = coordTemp ? Number(coordTemp.longitude) : NaN;
    const hasValidUserCoordinates = Number.isFinite(userLat) && Number.isFinite(userLon);
    const hasDateOrHour = Boolean( currentDate || currentHour );

    const targetDate = hasDateOrHour? currentDate ?? new Date(): null;
    const ymd = targetDate? formatDateToYMD(targetDate): null;
    const dayKey = targetDate? baseDays[targetDate.getDay()]: null;

    return miniRestos.filter(resto => {
        if (currentCity && !isProximitySearch) {
            if (resto._addressCityNorm !== normalizedCity && resto._visibleCityNorm !== normalizedCity) return false;
        }
        if (currentNeighborhood.length > 0 && !currentNeighborhood.includes(resto.neighborhood ?? "")) return false;
        
        if (proximityDistance !== null) {
            if (!coordTemp) return true;
            if (!hasValidUserCoordinates) return false;

            const restoLat =resto.location?.coordinates?.[1];
            const restoLon =resto.location?.coordinates?.[0];
            if (!Number.isFinite(restoLat) ||!Number.isFinite(restoLon)) return false;

            const distanceKm = getDistanceKm(userLat,userLon,restoLat as number,restoLon as number);
            if (distanceKm > proximityDistance) return false;
        }
        if (hasDateOrHour && ymd && dayKey) {
            const special = resto.special_date?.[ymd];
            if (special) {
                if (!special[0]) return false;
                if (currentHour) {
                    const slots = special[1];
                    if (!Array.isArray(slots)) return false;
                    const inRange = isBetween( slots[0][0],slots[0][1],currentHour) || ( slots.length > 1 && isBetween(slots[1][0],slots[1][1],currentHour) );
                    if (!inRange) return false;
                }
            } else {
                const schedule = resto.schedule?.[dayKey];
                if (!Array.isArray(schedule)) return false;

                if (currentHour) {
                    const inRange = isBetween(schedule[0][0],schedule[0][1],currentHour) || (schedule.length > 1 && isBetween(schedule[1][0],schedule[1][1],currentHour));
                    if (!inRange) return false;
                }
            }
        }
        return true;
    });
};
const NON_RESTAURANT_DATA_FILTERS = new Set(["Ouvert ce midi","Ouvert ce soir","Ouvert demain midi","Ouvert demain soir","Ouvert maintenant","À proximité"]);
const RATING_FILTERS = new Map<string, number>([["5 étoiles", 5],["4 étoiles", 4],["3 étoiles", 3]])
const TIME_SERVICE_FILTERS = new Map<string,"lunch" | "dinner" | "lunch_tomorrow" | "dinner_tomorrow" | "now">([
    ["Ouvert ce midi", "lunch"],
    ["Ouvert ce soir", "dinner"],
    ["Ouvert demain midi", "lunch_tomorrow"],
    ["Ouvert demain soir", "dinner_tomorrow"],
    ["Ouvert maintenant", "now"],
]);
const buildRestoMatcher = (
    currentFilter: FilterValue[],
    presentFilters: PresentSearchFilters
) => {
    const cuisineFilterSet = new Set(
        currentFilter.filter((filter): filter is string => typeof filter === "string" && isCuisineFilter(filter,presentFilters ))
    );
    const typeFilters = currentFilter.filter(
        (filter): filter is string => typeof filter === "string" && isTypedRestaurantFilter(filter)
    );
    return (resto: FilterableRestaurant): boolean => {
        const hasCuisineMatch = cuisineFilterSet.size > 0 && (resto.cuisines ?? []).some(cuisine => cuisineFilterSet.has(cuisine));
        const hasTypeMatch =
            typeFilters.length > 0 &&
            typeFilters.some(typeFilter =>
                (resto.type ?? []).some(rawType => restaurantTypeMatchesFilter(rawType,typeFilter))
            );

        for (const filter of currentFilter) {
            if (Array.isArray(filter)) {
                const [filterMin, filterMax] = filter;
                if (!resto.price_range) return false
                
                const [restaurantMin, restaurantMax] = resto.price_range;
                const hasNoMax = filterMax >= 100;
                const rangesOverlap = restaurantMax >= filterMin && (hasNoMax || restaurantMin <= filterMax);
                if (!rangesOverlap) return false;
                continue;
            }
            if (typeof filter === "number") continue;
            if (isCuisineFilter(filter, presentFilters)) {
                if (!hasCuisineMatch) return false;
                continue;
            }

            if (isTypedRestaurantFilter(filter)) {
                if (!hasTypeMatch)  return false;
                continue;
            }
            const service = TIME_SERVICE_FILTERS.get(filter);
            if (service) {
                if (!isStillValidForServiceFilter(resto, service)) return false;
                continue;
            }
            if (NON_RESTAURANT_DATA_FILTERS.has(filter)) continue;

            const minimumRating = RATING_FILTERS.get(filter);
            if (minimumRating !== undefined) {
                if (resto.note < minimumRating) return false;
                continue;
            }
            if (!(resto.filters?.includes(filter) ?? false)) return false;
        }

        return true;
    };
};
export const countMiniRestoResults = (contextualRestos: FilterableRestaurant[],currentFilter: FilterValue[],presentFilters: PresentSearchFilters): number => {
    if (currentFilter.length === 0) return contextualRestos.length;
    const matches = buildRestoMatcher(currentFilter,presentFilters);
    let count = 0;
    for (const resto of contextualRestos) {
        if (matches(resto)) count++;
    }
    return count;
}; 


export const getLockedFilterValues = (contextualRestos: FilterableRestaurant[],currentFilter: FilterValue[],presentFilters: PresentSearchFilters,candidates: string[]): Set<string> => {

    const locked = new Set<string>();
    if (candidates.length === 0) return locked;

    const selectedStrings = new Set(currentFilter.filter((filter): filter is string => typeof filter === "string"));

    const cuisineSet = new Set(presentFilters.cuisines);

    
    const currentCuisineFilters = currentFilter.filter((filter): filter is string => typeof filter === "string" && cuisineSet.has(filter));
    const currentTypeFilters = currentFilter.filter( (filter): filter is string => typeof filter === "string" && isTypedRestaurantFilter(filter));

    const otherCurrentFilters = currentFilter.filter(filter => {
        if (typeof filter !== "string") return true;
        if (cuisineSet.has(filter)) return false;
        if (isTypedRestaurantFilter(filter)) return false;

        return true;
    });

    const otherMatcher = buildRestoMatcher(otherCurrentFilters,presentFilters);

    const candidateInfos = candidates
        .filter(candidate => !selectedStrings.has(candidate))
        .map(candidate => {
            if (cuisineSet.has(candidate)) return {value: candidate,kind: "cuisine" as const};
            if (isTypedRestaurantFilter(candidate)) return {value: candidate,kind: "type" as const};

            const minimumRating = RATING_FILTERS.get(candidate);
            if (minimumRating !== undefined) return {value: candidate,kind: "rating" as const,minimumRating};
            if (NON_RESTAURANT_DATA_FILTERS.has(candidate)) return {value: candidate,kind: "ignored" as const};

            return {value: candidate,kind: "filter" as const};
        });

    const available = new Set<string>(); 
    for (const resto of contextualRestos) {
        if (!otherMatcher(resto)) {
            continue;
        }
        const currentCuisineMatches = currentCuisineFilters.length === 0 || (resto.cuisines ?? []).some(cuisine => currentCuisineFilters.includes(cuisine));
        const currentTypeMatches = currentTypeFilters.length === 0 || currentTypeFilters.some(typeFilter => (resto.type ?? []).some(rawType => restaurantTypeMatchesFilter(rawType,typeFilter)));

        const restoFilterSet = new Set(resto.filters ?? []);
        const restoCuisineSet = new Set(resto.cuisines ?? []);

        for (const candidate of candidateInfos) {
            if (available.has(candidate.value)) continue;
            if (candidate.kind === "cuisine") {
                if (!currentTypeMatches) continue;
                if (restoCuisineSet.has(candidate.value)) available.add(candidate.value);
                continue;
            }
           if (candidate.kind === "type") {
                if (!currentCuisineMatches) continue;
                const candidateTypeMatches =
                    (resto.type ?? []).some(rawType =>
                        restaurantTypeMatchesFilter(
                            rawType,
                            candidate.value
                        )
                    );
                if (candidateTypeMatches) available.add(candidate.value);
                continue;
            }
            if (!currentCuisineMatches ||!currentTypeMatches) continue;
            if (candidate.kind === "rating") {
                if (resto.note >=candidate.minimumRating) {
                    available.add(candidate.value);
                }
                continue;
            }
            if (candidate.kind === "ignored") {
                available.add(candidate.value);
                continue;
            }
            if (restoFilterSet.has(candidate.value)) {
                available.add(candidate.value);
            }
        }
        if (available.size ===candidateInfos.length) {
            break;
        }
    }

    for (const candidate of candidateInfos) {
        if (!available.has(candidate.value)) {
            locked.add(candidate.value);
        }
    } 

    return locked;
};
const getDistanceKm = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
) => {
    const a = 6378137;
    const e2 = 6.69437999014e-3;

    const toRad = (deg: number) => deg * Math.PI / 180;

    const lat1Rad = toRad(lat1);
    const lat2Rad = toRad(lat2);
    const meanLat = (lat1Rad + lat2Rad) / 2;

    const dLat = lat2Rad - lat1Rad;
    const dLon = toRad(lon2 - lon1);

    const sinLat = Math.sin(meanLat);
    const w = Math.sqrt(1 - e2 * sinLat * sinLat);

    const M = a * (1 - e2) / (w ** 3);
    const N = a / w;

    const dy = dLat * M;
    const dx = dLon * N * Math.cos(meanLat);

    return Math.hypot(dx, dy) / 1000;
};

export const getIndexableFilterValue = (page: IndexablePage): string | null => {
    const slug = page.filter_slug;
    if (slug?.startsWith("dish:") ||slug?.startsWith("regime:") ||slug?.startsWith("country:") ||slug?.startsWith("cultural-region:") ||slug?.startsWith("french-region:") || slug?.startsWith("concept-unique:")) return slug;
    return page.filter_name || null;
};



interface UseSwipeDownToCloseOptions {
    containerRef: React.MutableRefObject<HTMLDivElement | null>,
    onClose: () => void;
    isForFullBar?:boolean,
}

export const useSwipeDownToClose = ({
    containerRef,
    onClose,
    isForFullBar=false
}: UseSwipeDownToCloseOptions) => {
    const threshold = 0.1
    const dragStartYRef = useRef(0);
    const dragCurrentYRef = useRef(0);
    const isDraggingRef = useRef(false);
    const hasAutoClosedRef = useRef(false);

    const handleDragStart = (
        e: React.PointerEvent<HTMLDivElement>
    ) => {
        if ((e.target as HTMLElement).closest(".noSwipeClose")) {
            return;
        }
        hasAutoClosedRef.current = false;
        const container = containerRef.current;
        if (!container) return;

        isDraggingRef.current = true;
        dragStartYRef.current = e.clientY;
        dragCurrentYRef.current = 0;

        e.currentTarget.setPointerCapture(e.pointerId);

        container.style.transition = "none";
    };

    const handleDragMove = (
        e: React.PointerEvent<HTMLDivElement>
    ) => {
        if (!isDraggingRef.current) return;

        const container = containerRef.current;
        if (!container) return;

        const deltaY = Math.max(
            0,
            e.clientY - dragStartYRef.current
        );

        dragCurrentYRef.current = deltaY;

        container.style.transform =  `translateY(${deltaY}px)`;
        if(isForFullBar) {
            const panelHeight = container.getBoundingClientRect().height;
            const shouldClose = dragCurrentYRef.current >= panelHeight * 0.32;
            if (shouldClose) {
                hasAutoClosedRef.current = true
                container.style.transition =  "transform 0.18s cubic-bezier(0.32, 0.72, 0, 1)";
                container.style.transform = "translateY(100%)";
                window.setTimeout(() => {
                    onClose();

                    requestAnimationFrame(() => {
                        container.style.transform = "";
                        container.style.transition = "";
                    });
                }, 50);
            }
        }
    };

    const handleDragEnd = (
        e: React.PointerEvent<HTMLDivElement>
    ) => {
        if (hasAutoClosedRef.current) {
            if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                e.currentTarget.releasePointerCapture(e.pointerId);
            }
            return;
        }
        if (!isDraggingRef.current) return;

        isDraggingRef.current = false;

        const container = containerRef.current;
        if (!container) return;
        if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);

        const panelHeight = container.getBoundingClientRect().height;
        const shouldClose = dragCurrentYRef.current >= panelHeight * threshold;

        container.style.transition = "transform 0.25s cubic-bezier(0.32, 0.72, 0, 1)";

        if (shouldClose) {
            if(isForFullBar) {
                hasAutoClosedRef.current = true
                container.style.transform = "translateY(100%)";
                window.setTimeout(() => {
                    onClose();

                    requestAnimationFrame(() => {
                        container.style.transform = "";
                        container.style.transition = "";
                    });
                }, 50);
                return;
            }
            container.style.transform = "translateY(100%)";

            const handleTransitionEnd = () => {
                container.removeEventListener(
                    "transitionend",
                    handleTransitionEnd
                );
                if(isForFullBar) {
                    container.style.transform = "";
                    container.style.transition = "";
                }
                onClose();
            };

            container.addEventListener(
                "transitionend",
                handleTransitionEnd
            );
        } else {
            container.style.transform = "translateY(0)";

            const handleTransitionEnd = () => {
                container.removeEventListener(
                    "transitionend",
                    handleTransitionEnd
                );

                container.style.transform = "";
                container.style.transition = "";
            };

            container.addEventListener(
                "transitionend",
                handleTransitionEnd
            );
        }

        dragCurrentYRef.current = 0;
    };

    return {
        containerRef,
        dragHandlers: {
            onPointerDown: handleDragStart,
            onPointerMove: handleDragMove,
            onPointerUp: handleDragEnd,
            onPointerCancel: handleDragEnd,
        },
    };
};


interface Params {
  pathname: string;
  indexablePages: IndexablePage[];
  currentFilter: FilterValue[];
  currentCitySlug?: string | null;
}

export function useSeoSearchAnchor({
  pathname,
  indexablePages,
  currentFilter,
  currentCitySlug,
}: Params) {
    const currentIndexablePage = useMemo(() => {
        const currentPath = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
        return indexablePages.find((page) => {
            if (!page.slug_path) return false;
            const pagePath = page.slug_path === "/" ? "/" : page.slug_path.replace(/\/+$/, "");
            return pagePath === currentPath;
        }) ?? null;
    }, [indexablePages, pathname]);
    const currentSeoAnchorStillSelected = useMemo(() => {
        if (!currentIndexablePage) return false;
        const possibleValues = [currentIndexablePage.filter_name,currentIndexablePage.filter_slug,].filter((value): value is string => !!value);
        return possibleValues.some((value) =>
            currentFilter.some((filter) => typeof filter === "string" && filter === value)
        );
    }, [currentIndexablePage, currentFilter]);
    const isMajorFilter = (value: string) => {
        if (TypeResto.includes(value)) return true;
        if (value.startsWith("dish:") ||value.startsWith("regime:") ||value.startsWith("country:") ||value.startsWith("cultural-region:") ||value.startsWith("french-region:") ||value.startsWith("concept-unique:") ||value.startsWith("cuisine:") ||value.startsWith("cuisine-cultural-region:") ||value.startsWith("cuisine-french-region:")) {
            return true;
        }
        const matchingPage = indexablePages.find((page) => {
            const possibleValues = [
                page.filter_name,
                page.filter_slug,
            ].filter((value): value is string => !!value);

            return possibleValues.includes(value);
        });
        return (matchingPage?.filter_type === "type_resto" ||matchingPage?.filter_type === "cuisine" ||matchingPage?.filter_type === "regime");
    };
    const activeMajorFilters = useMemo(() => {
        return currentFilter
            .filter(
                (filter): filter is string =>
                    typeof filter === "string"
            )
            .filter((value) => isMajorFilter(value));
    }, [currentFilter, indexablePages]);
     const shouldKeepCurrentSeoPath = useMemo(() => {
        if (!currentSeoAnchorStillSelected) {
            return false;
        }
        const currentAnchorValues = [currentIndexablePage?.filter_name,currentIndexablePage?.filter_slug].filter((value): value is string => !!value);
        const selectedAnchorValue = currentAnchorValues.find((value) => currentFilter.some((filter) => typeof filter === "string" && filter === value));
        if (selectedAnchorValue &&isMajorFilter(selectedAnchorValue)) {
            const hasAnotherMajor = activeMajorFilters.some((value) => value !== selectedAnchorValue);
            if (hasAnotherMajor) return false;
        }
        if (currentIndexablePage?.page_kind === "filter" &&!currentCitySlug) return true;
        if (currentIndexablePage?.page_kind === "city_filter" &&currentCitySlug === currentIndexablePage.city_slug) return true;
        
        return false;
    }, [currentSeoAnchorStillSelected,currentIndexablePage,currentCitySlug,currentFilter,activeMajorFilters]);

    return {currentIndexablePage,currentSeoAnchorStillSelected,activeMajorFilters,shouldKeepCurrentSeoPath,};
}